import * as angular from 'angular';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { ViewDataModalActions, ViewDataModalSelectors } from '@mode/reports/domain';
import { ToastService } from '@mode/shared/util-toast';
import { AppStore } from '~/types/main.types';

import { Encoding } from '@mode-vegas/vegas';
import { ChartExportFacade } from '@mode/file-exports/domain';
import { ChartTypes, FeatureFlag, FeatureFlagsFacade, FlamingoTypes } from '@mode/shared/contract-common';
import {
  FLAMINGO_RESERVED_REFS,
  generateFormatFromFlatTableFormat,
  getDefaultFieldFormatMapFromColumns,
  getDefaultGeneralFormat,
  getDefaultTableColumnSettingsFromColumns,
  makePagingResult,
  prepareDataForCopying,
} from '@mode/shared/util-js';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';
import { MixableBaseController } from '../../common/component';

export class FlamingoViewDataGridController extends MixableBaseController {
  static $inject = ['$element', '$window', 'RxStoreService', 'FeatureFlagsFacade', 'ChartExportFacade', 'ToastService'];

  private toolbarEnabled: boolean;
  private sortingEnabled: boolean;

  columns$: Observable<FlamingoTypes.ResultColumns>;
  paging$: Observable<FlamingoTypes.PagingResult>;
  content$: Observable<FlamingoTypes.RecordsResult['content']>;
  format$: Observable<ChartTypes.ChartFormat>;
  loading$: Observable<boolean>;
  flamingoError$: Observable<boolean>;
  private fieldFormats$: Observable<ChartTypes.FieldFormatMap>;
  private dataToCopy$: Observable<string>;
  unaggregatedExportEnabled$: Observable<boolean>;
  private exportOverRowLimit$: Observable<boolean>;
  private exportOverRowLimitMessage$: Observable<string>;
  private chartNotExportable$: Observable<boolean>;

  public constructor(
    private $element: angular.IRootElementService,
    private $window: angular.IWindowService,
    private rxStore: AppStore,
    private featureFlagsFacade: FeatureFlagsFacade,
    private chartExportFacade: ChartExportFacade,
    private alertService: ToastService
  ) {
    super();

    this.unaggregatedExportEnabled$ = this.featureFlagsFacade.asObservable(FeatureFlag.UnaggregatedChartExport);
  }

  $onInit() {
    const planId$ = this.rxStore.selectNoEmpty(
      fromFlamingoSelectors.createPlanIdSelector(FLAMINGO_RESERVED_REFS.ViewData)
    );

    const state$ = planId$.pipe(
      switchMap((planId) => this.rxStore.select(fromFlamingoSelectors.createStateSelector(planId)))
    );

    this.loading$ = state$.pipe(map((state) => state === FlamingoTypes.PlanStatus.Loading));

    this.flamingoError$ = planId$.pipe(
      filter((planId) => planId != null),
      switchMap((id) => this.rxStore.select(fromFlamingoSelectors.createErrorsSelector(id))),
      map((error) => error != null),
      takeUntil(this.destroyed$)
    );

    this.columns$ = this.rxStore.selectNoEmpty(
      fromFlamingoSelectors.createColumnsSelector(FLAMINGO_RESERVED_REFS.ViewData)
    );

    const results$ = planId$.pipe(
      switchMap((planId) => this.rxStore.selectNoEmpty(fromFlamingoSelectors.createRecordsSelector(planId)))
    );

    this.paging$ = planId$.pipe(
      switchMap((planId) =>
        combineLatest(
          this.rxStore.select(ViewDataModalSelectors.selectPaging),
          this.rxStore.selectNoEmpty(fromFlamingoSelectors.createCountSelector(planId)),
          (paging, count) => makePagingResult(paging, count)
        )
      ),
      filter((p) => p != null),
      takeUntil(this.destroyed$)
    );

    this.content$ = results$.pipe(map((records) => records.content));

    this.format$ = this.columns$.pipe(
      map((columns) => {
        // Get the SQL results default and override sizingStyle
        let general = getDefaultGeneralFormat(ChartTypes.FlatTableDefaultGeneralFormat.Results);
        general = {
          ...general,
          sizingStyle: 'sizeToFit',
        };
        return generateFormatFromFlatTableFormat({
          general,
          columns: getDefaultTableColumnSettingsFromColumns(columns),
        });
      })
    );

    this.fieldFormats$ = this.columns$.pipe(map((columns) => getDefaultFieldFormatMapFromColumns(columns)));

    // Create copyable data
    this.dataToCopy$ = results$.pipe(map((data) => prepareDataForCopying(data)));

    const exportRowLimit$ = this.featureFlagsFacade.featureValueAsObservable<number>(FeatureFlag.CsvExportRowLimit);
    this.exportOverRowLimit$ = this.chartExportFacade.createIsTableOverLimit(FLAMINGO_RESERVED_REFS.ViewData);
    this.exportOverRowLimitMessage$ = exportRowLimit$.pipe(
      map(
        (limit) =>
          `<b class="headline">Export to CSV</b><br>Files will be truncated for tables with over ${limit / 1000}k rows`
      )
    );

    this.chartNotExportable$ = planId$.pipe(
      switchMap((planId) => this.rxStore.select(fromFlamingoSelectors.createReadySelector(planId))),
      map((ready) => !ready)
    );
  }

  setOffset(offset: number) {
    this.rxStore.dispatch(ViewDataModalActions.setOffset({ offset }));
  }

  exportCsv(encoding: Encoding) {
    this.chartExportFacade.exportViewDataCsv();
  }
}

export const FlamingoViewDataGridComponent: angular.IComponentOptions = {
  bindings: {
    toolbarEnabled: '<',
    sortingEnabled: '<',
  },

  controller: FlamingoViewDataGridController,
  controllerAs: 'ctrl',

  template: `
    <div class="view-data-table-container">
      <!-- Table Toolbar Begins -->

      <div class="view-data-table-toolbar" ng-if="ctrl.toolbarEnabled">
        <button class="query-run-action btn btn-default"
          ng-if="(ctrl.dataToCopy$ | async:this) && !(ctrl.unaggregatedExportEnabled$ | async:this)"
          copy-to-clipboard
          clipboard-text="ctrl.dataToCopy$ | async:this"
          clipboard-success="ctrl.alertService.success('Copied to clipboard')"
          tooltip-append-to-body="true"
          tooltip-popup-delay="750"
        >
          Copy page results
        </button>
        <capra-tooltip
          [tooltip-content]="ctrl.exportOverRowLimitMessage$ | async:this"
          tooltip-placement="bottom"
          tooltip-content-is-html="true"
          tooltip-interactive="true"
          tooltip-size="regular"
          [tooltip-is-disabled]="!(ctrl.exportOverRowLimit$ | async:this)"
          ng-if="ctrl.unaggregatedExportEnabled$ | async:this"
        >
          <capra-button
            icon="icon-export"
            icon-location="left"
            text="Export to CSV"
            class="download-button"
            size="small"
            color="neutral"
            variant="secondary"
            [disabled]="ctrl.chartNotExportable$ | async:this"
            (clicked)="ctrl.exportCsv()"
          ></capra-button>
        </capra-tooltip>
      </div>

      <!-- @Table Toolbar Ends -->

      <!-- Table Container Begins -->

      <mode-flat-table-ng
        ng-if="(ctrl.content$ | async:this)"
        [columns]="ctrl.columns$ | async:this"
        [content]="ctrl.content$ | async:this"
        [paging]="ctrl.paging$ | async:this"
        [format]="ctrl.format$ | async:this"
        [field-formats]="ctrl.fieldFormats$ | async:this"
        [expandable-cell-contents]="true"
        [resizable-columns]="true"
        [flat-sorting]="false"
        [token]="ctrl.queryToken"
        (offset-change)="ctrl.setOffset($event)"
        [hide-columns-count]="true"
      ></mode-flat-table-ng>

      <!-- @Table Container Ends -->

      <mode-table-loading
        ng-if="ctrl.loading$ | async:this"
        class="loading-state">
      </mode-table-loading>

      <div ng-if="ctrl.flamingoError$ | async:this" class="generic-error-state">
        <capra-icon icon="illo-error-dataset" height="72px" width="96px"></capra-icon>
        <div class="heading">There’s no data to view</div>
        <div class="subheading">Sorry, we ran into problems, this data can't be displayed.</div>
      </div>

      <!-- Intermediary Messages Begin -->

      <div ng-if="ctrl.dataset.state === 'empty'" class="error-message">
        <div class="message">No rows in dataset</div>
      </div>

      <!-- @Intermediary Messages End -->
    </div>
  `,
};
